/**
 * The triage outside React: the ideas others proposed and the questions DC
 * Data could not answer, which only a developer sees. Which of them the table
 * shows, their reasons and tools in the dashboard's language, the form that
 * puts one on the roadmap, the items one can merge into, and where the focus
 * goes when one leaves the table or a dialog about one closes.
 */

import type { RoadmapQuestionReason, RoadmapTriageEntry } from "@/lib/api";
import { apiErrorCode, apiErrorMessage } from "@/lib/api-errors";
import { i18n } from "@/lib/i18n";
import { getLanguage, isLanguage, type Language } from "@/lib/i18n/language";
import { toolText } from "@/lib/i18n/tools";
import { firstNameLabel, type NamedPerson, normalEmail } from "@/lib/people";
import { matchesSearch } from "@/lib/search";

import {
  type IdeaDraft,
  isRoadmapPriority,
  type MonthKind,
  ROADMAP_TOGGLES,
  type RoadmapFilters,
  SUMMARY_MAX,
  TITLE_MAX,
} from "./utils";

/** Why Claude could not answer a question, as the server sends it. */
export const QUESTION_REASONS = [
  "missing_tool",
  "missing_rights",
  "odoo_data_missing",
  "access_withheld",
  "other",
] as const satisfies readonly RoadmapQuestionReason[];

function isQuestionReason(value: string): value is (typeof QUESTION_REASONS)[number] {
  return (QUESTION_REASONS as readonly string[]).includes(value);
}

/** A reason in the dashboard's language ("Ontbrekende tool"); one it does not know as it came. */
export function questionReasonLabel(reason: string): string {
  return isQuestionReason(reason) ? i18n.t(`roadmap.triage.reasons.${reason}`) : reason;
}

/** A tool Claude tried, by its title in `language` ("Kranen oplijsten"), else by its name. */
export function triedToolTitle(name: string, language: Language): string {
  return toolText({ name, title: name, summary: "" }, language).title;
}

/**
 * What a developer does with an entry from its row: Naar backlog, Samenvoegen
 * met…, Sluiten or Verwijderen. All but Naar backlog ask in a dialog of the
 * roadmap view's own; Naar backlog opens the new idea's dialog.
 */
export type TriageAction = "accept" | "merge" | "close" | "delete";

/** An entry's kind as the texts key it: an idea, or a question (also for a kind not known yet). */
export function triageKind(entry: Pick<RoadmapTriageEntry, "kind">): "idea" | "question" {
  return entry.kind === "idea" ? "idea" : "question";
}

/**
 * A text on one line, cut at a word to the title's limit with an ellipsis:
 * the title "Naar backlog" starts from for an idea. The developer edits it
 * before saving.
 */
export function oneLineTitle(text: string): string {
  const line = text.replace(/\s+/g, " ").trim();
  if (line.length <= TITLE_MAX) {
    return line;
  }
  const cut = line.slice(0, TITLE_MAX - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > TITLE_MAX / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * The form "Naar backlog" opens with: the entry's priority (Middel when it
 * has none) and no Linear ids or effort yet, which only the developer knows.
 * An idea starts from its own texts, its title on one line and its text in
 * full as the summary, in the language it was written in (the dashboard's
 * when that is not known). A question, and an entry of a kind not known yet,
 * starts with no title or summary in the dashboard's language: it goes on the
 * roadmap only in the developer's own words, never as someone typed it, which
 * may name clients. The form shows the question above, for reference.
 */
export function triageDraft(
  entry: Pick<RoadmapTriageEntry, "kind" | "title" | "body" | "priority" | "language">,
): IdeaDraft {
  const priority = isRoadmapPriority(entry.priority) ? entry.priority : "medium";
  if (triageKind(entry) === "question") {
    return { title: "", summary: "", priority, linear: "", effort: null, language: getLanguage() };
  }
  return {
    title: oneLineTitle(entry.title),
    summary: entry.body.trim().slice(0, SUMMARY_MAX),
    priority,
    linear: "",
    effort: null,
    language: isLanguage(entry.language) ? entry.language : getLanguage(),
  };
}

/**
 * The people the triage names its submitters among, by email in lower case:
 * the dashboard's accounts and everyone who submitted one of `entries`, under
 * the name it was stored with, an account's name filling in a missing one.
 * Colleagues who use only Claude submit too without an account, so counting
 * the submitters keeps two of them who share a first name apart
 * (`submitterLabel`). Pass every entry, not only those a search shows, so a
 * name does not change with the search.
 */
export function triagePeople(
  entries: readonly Pick<RoadmapTriageEntry, "submitted_by" | "submitted_by_name">[],
  people: ReadonlyMap<string, NamedPerson>,
): Map<string, NamedPerson> {
  const known = new Map<string, NamedPerson>();
  for (const entry of entries) {
    const email = normalEmail(entry.submitted_by);
    known.set(email, {
      email,
      name: entry.submitted_by_name ?? known.get(email)?.name ?? people.get(email)?.name,
    });
  }
  for (const [email, person] of people) {
    if (!known.has(email)) {
      known.set(email, person);
    }
  }
  return known;
}

/**
 * Who submitted an entry, by first name (`firstNameLabel` over `people`, the
 * triage's `triagePeople`): from the name it was stored with, else the name
 * their account has now, else their email's part before the @.
 */
export function submitterLabel(
  entry: Pick<RoadmapTriageEntry, "submitted_by" | "submitted_by_name">,
  people: ReadonlyMap<string, NamedPerson>,
): string {
  return firstNameLabel(people.values())({
    email: entry.submitted_by,
    name: entry.submitted_by_name ?? people.get(normalEmail(entry.submitted_by))?.name,
  });
}

/**
 * The entries the table shows, newest first as the server sends them: in
 * the current month and later ones, which show the ideas, and never in a past
 * month. A search looks in the title, the text, who submitted it and Claude's
 * note. A priority or a quick filter picks items, so while one is on, none
 * shows.
 */
export function shownTriage(
  entries: readonly RoadmapTriageEntry[],
  filters: RoadmapFilters,
  kind: MonthKind,
): RoadmapTriageEntry[] {
  if (
    kind === "past" ||
    filters.priority !== "all" ||
    ROADMAP_TOGGLES.some((toggle) => filters[toggle])
  ) {
    return [];
  }
  return entries.filter((entry) =>
    matchesSearch(
      [entry.title, entry.body, entry.submitted_by_name, entry.submitted_by, entry.detail],
      filters.search,
    ),
  );
}

/**
 * The items an entry can merge into: every item on the roadmap that is not
 * done, in the roadmap's order. The server refuses a done one.
 */
export function mergeTargets<T extends { status: string }>(items: readonly T[]): T[] {
  return items.filter((item) => item.status !== "done");
}

/**
 * What to say when a merge failed. The item chosen may have been finished or
 * deleted since the page loaded: say so, and that another one is needed (the
 * refetch drops it from the list). Anything else as `apiErrorMessage` words it.
 */
export function mergeFailureMessage(error: unknown): string {
  switch (apiErrorCode(error)) {
    case "roadmap_item_done":
      return i18n.t("roadmap.triage.mergeItemDone");
    case "roadmap_item_not_found":
      return i18n.t("roadmap.triage.mergeItemGone");
    default:
      return apiErrorMessage(error, "mergeRoadmapTriage");
  }
}

/**
 * Whether the entry a dialog is about has left the triage: put on the roadmap
 * or closed by another developer, or from Claude, since the page loaded. The
 * dialogs refetch the roadmap as they open, so it shows before anything is
 * typed. False with no entry, or while the triage is not known.
 */
export function triageEntryGone(
  entry: Pick<RoadmapTriageEntry, "id"> | null,
  triage: readonly Pick<RoadmapTriageEntry, "id">[] | null | undefined,
): boolean {
  return entry !== null && triage != null && !triage.some((open) => open.id === entry.id);
}

/** The fold toggle of a group of the table, by its `data-group`. */
function groupToggle(root: ParentNode | null | undefined, group: string): HTMLElement | null {
  return (
    root?.querySelector<HTMLElement>(
      `tbody[data-group="${group}"] th[scope="rowgroup"] button[aria-expanded]`,
    ) ?? null
  );
}

/**
 * After an entry left the table with the focus on it (put on the roadmap,
 * merged, closed or deleted): the focus goes to the toggle of Triage, or to
 * the ideas' toggle when it was the last entry. Whether one took it.
 */
export function focusTriageToggle(root: ParentNode | null | undefined): boolean {
  const toggle = groupToggle(root, "triage") ?? groupToggle(root, "idea");
  toggle?.focus();
  return toggle !== null;
}

/**
 * Where the focus goes as a dialog about an entry closes: back to what opened
 * it while that is still on the page (the row's action); else, when the
 * row's phone menu opened it, to that menu's button while the row is there;
 * else, the entry having left, to the group's toggle (`focusTriageToggle`).
 * Whether the focus went somewhere, so the dialog leaves it there.
 */
export function returnTriageFocus(
  opener: HTMLElement | null,
  entryId: string,
  root: ParentNode = document,
): boolean {
  if (opener?.isConnected) {
    opener.focus();
    return true;
  }
  const id = entryId.replace(/["\\]/g, "\\$&");
  const row = root.querySelector(`[data-triage="${id}"]`)?.closest("tr");
  const menu = [...(row?.querySelectorAll<HTMLElement>('button[aria-haspopup="menu"]') ?? [])].find(
    (button) => button.getClientRects().length > 0,
  );
  if (menu) {
    menu.focus();
    return true;
  }
  return focusTriageToggle(root);
}
